import {ANTHROPIC_FUNCTION_PATH,isAnthropicFunctionAvailable} from './netlifyFunction.js';

const API_URL='https://api.anthropic.com/v1/models';
const API_VERSION='2023-06-01';
const FUNCTION_MODELS_URL=`${ANTHROPIC_FUNCTION_PATH}/v1/models`;

// Anthropic only returns CORS headers for browser requests that opt in with
// this header. The SDK adds it automatically for Messages calls, but our raw
// fetch() to the Models API must add it explicitly or the browser blocks it.
const buildHeaders=(key,direct)=> {
  const headers={
    'x-api-key': key,
    'anthropic-version': API_VERSION,
    'accept': 'application/json',
  };
  if (direct) headers['anthropic-dangerous-direct-browser-access']='true';
  return headers;
};


const rankModel=(model)=> {
  const name=`${model.id} ${model.display_name || ''}`.toLowerCase();
  const family=name.includes('sonnet') ? 3 : name.includes('haiku') ? 2 : name.includes('opus') ? 1 : 0;
  const date=Date.parse(model.created_at || '') || 0;
  return family * 1e15 + date;
};

export const recommendModel=(models)=> (
  [...models].sort((a,b)=> rankModel(b) - rankModel(a))[0]?.id || ''
);

const normaliseProxy=(proxyUrl)=> {
  const trimmed=proxyUrl?.trim();
  if (!trimmed) return null;
  try {
    const url=new URL(trimmed);
    return ['https:','http:'].includes(url.protocol) ? url.toString().replace(/\/+$/,'') : null;
  } catch {
    return null;
  }
};

/**
 * Try the Netlify serverless function for the Models API.
 *
 * Returns null ONLY when the function is unreachable (not deployed,
 * network error, or SPA fallback intercepted the request). In that case
 * the caller may fall back to a direct browser call.
 *
 * Throws for genuine API errors (401, 403, 500) so the user sees the
 * real problem instead of a misleading "cannot reach" message.
 */
const tryFunctionModels=async (key,signal)=> {
  if (!(await isAnthropicFunctionAvailable())) return null;
  try {
    const url=new URL(FUNCTION_MODELS_URL,window.location.origin);
    url.searchParams.set('limit','100');
    const response=await fetch(url,{
      signal: signal || undefined,
      headers: buildHeaders(key,false),
    });

    if (response.status===404) return null;

    // If the function is not deployed, the SPA fallback answers with
    // index.html (HTML, status 200). Treat that as "unreachable".
    const contentType=response.headers.get('content-type') || '';
    if (!contentType.includes('application/json')) return null;

    const body=await response.json().catch(()=> null);
    if (!body) return null;

    if (response.status===401 || response.status===403) {
      throw new Error(body.error?.message || `Anthropic rejected the API key (${response.status}).`);
    }
    if (!response.ok) {
      throw new Error(body.error?.message || `Anthropic Models API returned ${response.status}.`);
    }
    if (!Array.isArray(body.data)) {
      throw new Error('Anthropic returned an invalid models response.');
    }

    const models=body.data.filter((model)=> typeof model.id==='string');
    if (!models.length) throw new Error('No models are listed for this API key.');
    return models.sort((a,b)=> rankModel(b) - rankModel(a));
  } catch (error) {
    if (error.name==='AbortError') throw error;
    if (error instanceof TypeError) return null;
    throw error;
  }
};

export const fetchAvailableModels=async (apiKey,signal=null,proxyUrl=null)=> {
  const key=apiKey?.trim();
  if (!key?.startsWith('sk-ant-')) {
    throw new Error('Enter a valid Anthropic API key to load models.');
  }

  const proxy=normaliseProxy(proxyUrl);

  if (!proxy) {
    const functionModels=await tryFunctionModels(key,signal);
    if (functionModels) return functionModels;
  }

  const target=proxy ? `${proxy}/v1/models` : API_URL;
  const models=[];
  let afterId='';

  do {
    const url=new URL(target);
    url.searchParams.set('limit','100');
    if (afterId) url.searchParams.set('after_id',afterId);

    let response;
    try {
      response=await fetch(url,{
        signal: signal || undefined,
        headers: buildHeaders(key,!proxy),
      });
    } catch (error) {
      if (error.name==='AbortError') throw error;
      const via=proxy ? ' through the configured proxy' : ' directly';
      throw new Error(`Could not reach the Anthropic Models API${via}. Check your connection and browser access.`);
    }

    const body=await response.json().catch(()=> ({}));
    if (!response.ok) {
      throw new Error(body.error?.message || `Anthropic Models API returned ${response.status}.`);
    }
    if (!Array.isArray(body.data)) {
      throw new Error('Anthropic returned an invalid models response.');
    }

    models.push(...body.data.filter((model)=> typeof model.id==='string'));
    afterId=body.has_more ? body.last_id : '';
    if (body.has_more && !afterId) {
      throw new Error('Anthropic returned an incomplete models page.');
    }
  } while (afterId);

  if (!models.length) throw new Error('No models are listed for this API key.');
  return models.sort((a,b)=> rankModel(b) - rankModel(a));
};